import type { CSSProperties, ReactNode } from 'react'
import type { DesignSystemColorFamily } from '../tokens/colors'
import { colorFamilies } from '../tokens/colors'

/** Provider-driven schedule card color (maps to DS token families). */
export type ScheduleCardColor = DesignSystemColorFamily

export type ScheduleCardColorTokens = {
  surface: string
  border: string
  header: string
  text: string
  headerText: string
}

export type ScheduleCardProps = {
  /** Appointment time range, e.g. "11:00 AM - 1:00 PM". */
  timeRange?: string
  /** Patient display name. */
  patientName?: string
  /** Appointment type / procedure label. */
  appointmentType?: string
  /**
   * Color family for the card. In the Appointment calendar this follows the
   * provider’s assigned color.
   */
  color?: ScheduleCardColor
  /**
   * Optional explicit colors (e.g. map from `provider.provider_color`).
   * Overrides the `color` family tokens when set.
   */
  colors?: Partial<ScheduleCardColorTokens>
  /** Status / meta icons shown in the header (right). */
  headerIcons?: ReactNode
  /** Tooth icon for appointments created or updated by the AI receptionist. */
  aiAssisted?: boolean
  /** Hover text for the AI receptionist icon. */
  aiHint?: string
  showTime?: boolean
  showAppointmentType?: boolean
  className?: string
  style?: CSSProperties
  onClick?: () => void
}

export const SCHEDULE_CARD_COLORS: ScheduleCardColor[] = [...colorFamilies]

export const SCHEDULE_CARD_COLOR_LABELS: Record<ScheduleCardColor, string> =
  Object.fromEntries(
    SCHEDULE_CARD_COLORS.map((color) => [
      color,
      color
        .split('-')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' '),
    ]),
  ) as Record<ScheduleCardColor, string>

/**
 * Token shade recipe per family, aligned with Schedule Cards
 * (pale-green / purple / yellow examples) and reusable for other families.
 * Cards keep the same colors on dark page/section backgrounds.
 */
export const getScheduleCardColorTokens = (
  color: ScheduleCardColor = 'pale-green',
): ScheduleCardColorTokens => {
  const ds = (shade: number): string => `var(--ds-${color}-${shade})`

  switch (color) {
    case 'purple':
    case 'indigo':
      return {
        surface: ds(200),
        border: ds(400),
        header: ds(600),
        text: ds(700),
        headerText: 'var(--ds-white)',
      }
    case 'yellow':
    case 'lime-green':
      return {
        surface: ds(200),
        border: ds(400),
        header: ds(400),
        text: ds(700),
        headerText: ds(700),
      }
    case 'gray':
      return {
        surface: ds(100),
        border: ds(300),
        header: ds(500),
        text: ds(700),
        headerText: 'var(--ds-white)',
      }
    default:
      return {
        surface: ds(100),
        border: ds(400),
        header: ds(500),
        text: ds(700),
        headerText: 'var(--ds-white)',
      }
  }
}

export const SCHEDULE_CARD_DEMO_TIME = '11:00 AM - 1:00 PM'
export const SCHEDULE_CARD_DEMO_PATIENT = 'Sophie M. Markus'
export const SCHEDULE_CARD_DEMO_TYPE = 'Periodontal (Prosthesis removal)'

/**
 * Maps Appointment `provider.provider_color.label` to a ScheduleCard color family.
 * Falls back to pale-green.
 */
export const mapProviderLabelToScheduleCardColor = (
  label?: string,
): ScheduleCardColor => {
  const normalized = (label ?? '').toLowerCase()

  if (normalized.includes('bright green') || normalized === 'green') {
    return normalized.includes('bright') ? 'vivid-green' : 'pale-green'
  }
  if (normalized.includes('bright blue') || normalized === 'blue') {
    return 'blue'
  }
  if (normalized.includes('bright purple') || normalized === 'purple') {
    return 'purple'
  }
  if (normalized.includes('bright red') || normalized === 'red') {
    return 'red'
  }
  if (normalized === 'teal') return 'teal'
  if (normalized === 'yellow') return 'yellow'
  if (normalized === 'orange' || normalized === 'brown') return 'orange'
  if (normalized === 'pink') return 'pink'

  return 'pale-green'
}
